import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  SimpleChanges,
  NgZone,
  input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Renderer2,
  OnChanges,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Viewport, IViewportOptions, ISnapZoomOptions } from 'pixi-viewport';
import {
  Application,
  Renderer,
  Container,
  Graphics,
  Spritesheet,
  Sprite,
  FederatedPointerEvent,
  Point,
  Text,
  Texture,
  TextStyle,
  DisplayObject,
  Rectangle,
} from 'pixi.js';
import {
  catchError,
  filter,
  ReplaySubject,
  takeUntil,
  EMPTY,
  Subject,
  fromEvent,
  take,
  distinctUntilChanged,
} from 'rxjs';

import { delayFor } from 'app/core/utilities/async-utils';
import { formatUserName } from 'app/core/utilities/user-util';
import { Loggable } from 'app/utils/logging/loggable';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';

import { BadgingService } from '../services/badging/badging.service';
import { BrowserService } from '../services/browser.service';
import { ChatService } from '../services/chat.service';
import { DeviceService } from '../services/device/device.service';
import { EmoteService } from '../services/emote/emote.service';
import { ZIndex } from '../social-pixi-canvas/constants';
import { ExtendedISnapZoomOptions } from '../social-pixi-canvas/scene-manager';

import { RtcStatsService } from './../services/rtc-stats.service';
import { OnboardingPlayer } from './onboarding-player';
import '@pixi/gif';
import { SceneService } from '../services/scene.service';
import { initDevtools } from '@pixi/devtools';
import { LaunchDarklyFlags, PixiInspector } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { COLORS } from '../constants';
import { ZoomDirection } from '../social-pixi-canvas/zoom-controller';
import { userInfo } from 'app/modules/scootaverse/scootaverse-badging/scootaverse-badging.component';
import { socialAppSelectors } from '../state/app';

const MAP_BORDER = 1;
const BORDER_COLOR = 0xffffff;
const ZOOM_OFFSET = 0.1;

@Component({
  selector: 'app-pixi-onboarding-experience',
  templateUrl: './pixi-onboarding-experience.component.html',
  styleUrl: './pixi-onboarding-experience.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PixiOnboardingExperienceComponent extends Loggable implements OnInit, OnDestroy, OnChanges {
  private elementRef = inject(ElementRef);
  private store = inject(Store);
  private browserService = inject(BrowserService);
  private chatService = inject(ChatService);
  private badgingService = inject(BadgingService);
  private rtcStatsService = inject(RtcStatsService);
  private emoteService = inject(EmoteService);
  private deviceService = inject(DeviceService);
  private ngZone = inject(NgZone);
  private sceneService = inject(SceneService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private badgeDomElement: HTMLElement | null = null; // <<< Add this declaration
  private parentElement: HTMLElement | null = null;

  protected app!: Application;
  public element?: HTMLElement;
  public mainPlayer?: OnboardingPlayer;
  public mockPlayer?: OnboardingPlayer;
  public viewport!: Viewport;
  public roomContainer!: Container;
  private cameraStream: MediaStream = DUMMY_VIDEO_STREAM;
  private windowHeight?: number;
  private windowWidth?: number;
  public customCursorVisible = false;
  private _zoom = 1;
  private maxZoom = 1;
  private minZoom!: number;
  private mockPlayerVideo!: HTMLVideoElement;
  private groupingContainer!: Container;
  private stageMagicContainer!: Container;
  private keyboardContainer!: Container;
  private mouseContainer!: Container;
  public readonly hideZoom = signal<boolean>(true);
  public readonly inDisabled = signal<boolean>(true);
  public readonly outDisabled = signal<boolean>(true);
  public zoomDirection: ZoomDirection = { value: 'none' };
  private zoomIntervalId!: NodeJS.Timeout | null;
  public w = 4000;
  public h = 4000;
  public autoZoomEnabled = false;
  public autoZoomFlag = false;

  public snapZoomOptions: ExtendedISnapZoomOptions = {
    removeOnComplete: true,
    removeOnInterrupt: false,
    time: 1000,
    ease: 'easeInOutSine',
  };

  public zoomDirection$ = new Subject<ZoomDirection>();
  private zoomTimeout: number | undefined;
  private isZooming = false;

  private keyMap: Record<string, string> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    w: 'up',
    s: 'down',
    a: 'left',
    d: 'right',
  };

  public readonly showMockBadge = signal<boolean>(false);
  // Handle info por main user badge
  public readonly showBadge = input<boolean>(false);
  public readonly userInfo = input<userInfo | undefined>(undefined);
  public readonly user = input<User | undefined>();
  public readonly social = input<Social | undefined>();
  public readonly showGroupingGif = input<boolean>(false);
  public readonly showStageMagicGif = input<boolean>(false);
  public readonly toggleMiniMap = input<boolean>(false);
  public readonly step = input<string | undefined>();

  private sprites!: Spritesheet;

  public get zoom(): number {
    if (!this.viewport) {
      return 1;
    }
    return this.viewport.scale.x;
  }
  public set zoom(value: number | { zoom: number; center: Point }) {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      this.viewport.snapZoom({
        time: 1000,
        width: value,
        ease: 'linear',
      });
    } else {
      const zoom = Math.floor(value.zoom * 100) / 100;
      this.viewport.snapZoom({
        time: 1000,
        width: this.viewport.screenWidth / zoom,
        // center: value.center,
        ease: 'linear',
      });
    }
  }

  // Constants for player collision detection
  private readonly PLAYER_RADIUS = 60; // Actual radius of each player (visual size)
  private readonly COLLISION_PADDING = 10; // Extra padding to maintain between player edges
  private readonly COLLISION_PUSH_STRENGTH = 12; // How strongly to push players apart when colliding

  /**
   * Checks if two players are colliding and prevents overlapping by pushing them apart
   * This uses edge-to-edge collision detection rather than center-to-center
   * @param player1 First player to check
   * @param player2 Second player to check
   * @returns True if players were colliding and adjusted, false otherwise
   */
  private preventPlayerCollision(player1: OnboardingPlayer, player2: OnboardingPlayer): boolean {
    if (!player1 || !player2) {
      return false;
    }

    // Calculate distance between player centers
    const dx = player2.x - player1.x;
    const dy = player2.y - player1.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Calculate minimum distance needed to prevent edge overlap
    const minDistance = this.PLAYER_RADIUS * 4 + this.COLLISION_PADDING;

    // Check if players' edges are overlapping
    if (distance < minDistance) {
      // Normalize direction vector
      const nx = dx / distance || 0; // Avoid division by zero
      const ny = dy / distance || 0;

      // Calculate overlap amount and apply push strength
      const overlap = (minDistance - distance) * this.COLLISION_PUSH_STRENGTH;

      // Set the new target position for player1, pushing away from player2
      player1.setTargetPosition(player1.targetX - nx * overlap, player1.targetY - ny * overlap);

      // Log collision for debugging
      this.debug(
        `Edge collision detected - Distance: ${distance.toFixed(2)}, Min required: ${minDistance}, Overlap: ${overlap.toFixed(2)}`,
      );
    }

    return false;
  }

  // region lifecycle
  public constructor() {
    super();

    this.setDisplayName('PixiOnboardingExperienceComponent');
    // if (Assets.loader) {
    // Assets.loader.parsers.push(loadKTX2 as any);
    // }
    this.deviceService.cameraStream$
      .pipe(
        filter(notNil),
        catchError((error: unknown) => {
          this.error('Error with video stream', error);
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((stream) => {
        this.cameraStream = stream ?? DUMMY_VIDEO_STREAM;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_auto_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((autoZoomFlag) => {
        this.autoZoomEnabled = autoZoomFlag;
        this.autoZoomFlag = autoZoomFlag;
        if (this.sceneService.sceneManager) {
          this.sceneService.sceneManager.autoZoomEnabled = autoZoomFlag;
        }
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.toggleOnboardingMockBadge)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((toggleOnboardingMockBadge) => {
        this.showMockBadge.set(toggleOnboardingMockBadge);

        if (toggleOnboardingMockBadge) {
          this.resetMockPlayerBadgePosition();
        }
      });
  }

  public ngOnInit(): void {
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;
    this.element = this.elementRef.nativeElement.querySelector('#pixiOnboardingExperience');

    // Get parent element
    this.parentElement = this.renderer.parentNode(this.elementRef.nativeElement);
    if (!this.parentElement) {
      this.error('Could not find parent element for MutationObserver.');
      return;
    }

    void this.ngZone.runOutsideAngular(() => {
      this.app = new Application({
        resizeTo: this.element,
        autoDensity: true,
        backgroundAlpha: 0,
        resolution: window.devicePixelRatio,
        autoStart: true,
        powerPreference: 'high-performance',
        eventMode: 'passive',
        antialias: false,
        eventFeatures: {
          move: true,
          globalMove: false,
          click: true,
          wheel: true,
        },
        width: this.windowWidth,
        height: this.windowHeight,
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_inspector)
        .pipe(
          take(1),
          filter((pixi_inspector) => pixi_inspector === PixiInspector.ONBOARDING),
        )
        .subscribe(() => {
          void initDevtools({
            app: this.app,
          });
        });

      // const texture = await Assets.load(sprites.meta.image);
      // this.sprites = new Spritesheet(texture, sprites);
      // await this.sprites.parse();
      this.sprites = this.sceneService.sceneManager!.sprites;

      this.app.stage.eventMode = 'static';

      this.elementRef.nativeElement.appendChild(this.app.view);

      const viewportOptions: IViewportOptions = {
        screenWidth: this.windowWidth,
        screenHeight: this.windowHeight,
        worldWidth: this.w,
        worldHeight: this.h,
        // events: this.app.renderer.events,
      };
      this.viewport = new Viewport(viewportOptions);
      this.roomContainer = new Container();
      this.roomContainer.name = 'room-container';
      this.roomContainer.sortableChildren = true;
      this.viewport.sortableChildren = true;
      this.viewport.addChild(this.roomContainer);

      // Set appropriate min and max zoom levels
      this.minZoom = 0.4; // Allow zooming out to see the entire scene
      this.maxZoom = 2.0; // Allow closer inspection of elements

      this.app.stage.addChild(this.viewport);

      // Example User
      this.mockPlayerVideo = document.createElement('video');

      this.mockPlayerVideo.srcObject = DUMMY_VIDEO_STREAM;
      this.mockPlayerVideo.width = 220;
      this.mockPlayerVideo.height = 220;
      this.mockPlayerVideo.muted = true;

      this.addMockPlayer(this.mockPlayerVideo);

      // Add My Player
      const video = document.createElement('video');

      video.srcObject = this.cameraStream;
      video.width = 220;
      video.height = 220;
      video.muted = true;

      if (video) {
        this.addMyPlayer(video);
      }

      this.createKeyboardComponent();
      this.createMouseComponent();

      this.createGroupingVideoContainer();
      this.createStateMagicVideoContainer(false);

      // await this.createGroupingContainer();
      // await this.createStateMagicContainer();

      this.app.stage.eventMode = 'static';

      // Make sure the whole canvas area is interactive
      this.app.stage.hitArea = this.app.screen;

      // Follow the pointer, this could be used for custom cursor
      // this.app.stage.addEventListener('pointermove', (e) => {
      //   if (this.cursor) {
      //     this.cursor.position.copyFrom(e.global);
      //   }
      // });
      // go where the pointer clicks
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          const width = window.innerWidth;
          const height = window.innerHeight;

          // Update window dimensions
          this.windowWidth = width;
          this.windowHeight = height;

          // Trigger resize logic
          this.resizeWorld(width, height);
        });

      // Fit all content after initialization is complete
      // setTimeout(() => {
      //   void this.toggleAutoZoom(true);
      // }, 2000); // Give time for all elements to be loaded and positioned
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.showGroupingGif) {
      if (changes.showGroupingGif.currentValue) {
        this.showGroupingRegion();
      } else {
        this.hideGroupingRegion();
      }
    }

    if (changes && changes.showStageMagicGif) {
      if (changes.showStageMagicGif.currentValue) {
        this.checkIfStageMagicExist();
      } else {
        this.hideStageMagicRegion();
      }
    }

    if (changes && changes.toggleMiniMap) {
      if (changes.toggleMiniMap.currentValue) {
        this.showMiniMap();
      } else {
        this.hideMiniMap();
      }
    }

    if (changes && changes.step && changes.step.currentValue) {
      void this.updateMockPlayerVideo(changes.step.currentValue);
      this.handleStep(changes.step.currentValue);

      // Fit all content when step changes
      window.setTimeout(() => {
        void this.zoomToFitAll();
      }, 1000);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.viewport.removeChildren();
    this.roomContainer.destroy();
    this.app.destroy(true, {
      children: true,
      texture: true,
    });
    this.mockPlayer?.clearVideoElement();
    this.mockPlayer?.destroy();
    this.mainPlayer?.destroy();
  }

  // endregion lifecycle

  // region mini map

  private hideMiniMap() {
    if (!this.app || !this.app.stage) {
      return;
    }
    const parentContainer = this.app.stage.getChildByName('mini-map-parent-container');
    if (parentContainer) {
      parentContainer.visible = false;
    }
  }

  private showMiniMap() {
    // Define map dimensions and padding
    const mapWidth = 400;
    const mapHeight = 400;
    const selectorHeight = 40;
    const gap = 10;
    const padding = 20;
    const toolbarHeight = 65; // Height of the toolbar at the bottom

    // Calculate total container dimensions
    const totalWidth = mapWidth;
    const totalHeight = mapHeight + selectorHeight + gap + toolbarHeight;

    // Calculate position for the parentContainer's top-left corner
    const targetX = this.app.screen.width - totalWidth - padding;
    const targetY = this.app.screen.height - totalHeight - padding;

    const parentContainer = new Container();
    parentContainer.name = 'mini-map-parent-container';
    parentContainer.sortableChildren = true;
    // Set parentContainer position
    parentContainer.x = targetX;
    parentContainer.y = targetY;

    const mapContainer = new Container();
    mapContainer.name = 'mini-map-container';
    mapContainer.sortableChildren = true;
    // mapContainer position is relative to parentContainer, implicitly (0,0)

    // --- Adjust internal element positioning relative to parentContainer (0,0) ---
    const selectorX = 0; // Selector starts at the left edge of the parent container
    const selectorYPos = 0; // Selector starts at the top edge of the parent container

    const selectorBackground = new Graphics()
      .beginFill(0xffffff, 1)
      // Draw relative to parentContainer's origin (0,0)
      .drawRoundedRect(selectorX, selectorYPos, totalWidth, selectorHeight, 8)
      .endFill();
    selectorBackground.name = 'room-selector-background';
    selectorBackground.zIndex = 3;

    const arrowSize = 12;
    // Position arrow relative to selectorBackground, near the right edge
    const arrowX = selectorX + totalWidth - arrowSize - 10;
    const arrowYPos = selectorYPos + selectorHeight / 2;
    const arrow = new Graphics()
      .beginFill(0x000000)
      .moveTo(arrowX, arrowYPos - arrowSize / 2)
      .lineTo(arrowX + arrowSize, arrowYPos - arrowSize / 2)
      .lineTo(arrowX + arrowSize / 2, arrowYPos + arrowSize / 2)
      .closePath()
      .endFill();
    arrow.name = 'room-selector-arrow';
    arrow.zIndex = 4;

    const style = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0x000000,
      align: 'left',
    });

    const roomText = new Text('Main Room', style);
    roomText.name = 'room-selector-text';
    // Position text relative to selectorBackground
    roomText.x = selectorX + 20;
    roomText.y = selectorYPos + (selectorHeight - 16) / 2; // Center vertically
    roomText.zIndex = 4;

    // Map position relative to parentContainer
    const mapX = 0;
    const mapY = selectorHeight + gap; // Below selector + gap

    const mapMask = new Graphics()
      .beginFill(COLORS.maskColor)
      .drawRoundedRect(mapX, mapY, mapWidth, mapHeight, 15)
      .endFill();

    const mapBorders = new Graphics().lineStyle(3, 0xffffff).drawRoundedRect(mapX, mapY, mapWidth, mapHeight, 15);
    mapBorders.name = 'mini-map-border';
    mapBorders.zIndex = 3;

    mapContainer.mask = mapMask; // Apply mask to mapContainer

    selectorBackground.eventMode = 'static';
    selectorBackground.cursor = 'pointer';
    selectorBackground.on('pointerdown', () => {
      // Handle selector click event
    });

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/mini_map.mp4';
    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();
      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);
      videoSprite.name = 'mini-map-video-sprite';
      // Position video sprite relative to parentContainer
      videoSprite.width = mapWidth;
      videoSprite.height = mapHeight;
      videoSprite.x = mapX;
      videoSprite.y = mapY;
      videoSprite.zIndex = 2;

      // Add children to containers
      mapContainer.addChild(videoSprite, mapBorders, mapMask);
      parentContainer.addChild(mapContainer, selectorBackground, arrow, roomText);
      this.app.stage.addChild(parentContainer);
    });
  }

  // endregion mini map

  // region utilities

  public formatUserName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  private resizeWorld(width: number, height: number): void {
    if (!this.viewport) {
      return;
    }
    if (width && height) {
      this.info(`Did resize world to width: ${width} and height: ${height}`);
    }

    // Update application renderer size
    this.app.renderer.resize(width, height);

    // Update viewport world and screen sizes
    this.viewport.resize(width, height, this.w, this.h);
    void this.zoomToFitAll();
    // Trigger change detection
    this.changeDetectorRef.markForCheck();
  }

  private keydownListener = (e: KeyboardEvent) => {
    const key = this.keyMap[e.key];
    switch (key) {
      case 'up':
        void this.activeKeyboard('keyboard_arrow_up', e.type);
        break;
      case 'down':
        void this.activeKeyboard('keyboard_arrow_down', e.type);
        break;
      case 'left':
        void this.activeKeyboard('keyboard_arrow_left', e.type);
        break;
      case 'right':
        void this.activeKeyboard('keyboard_arrow_right', e.type);
        break;
    }
  };

  private async activeKeyboard(label: string, event: string): Promise<void> {
    const keyboardSprite = this.keyboardContainer.getChildByName(label) as Sprite;
    if (event === 'keydown') {
      keyboardSprite.tint = '0x0c58c6';
    } else {
      await delayFor(500);
      keyboardSprite.tint = '0xf2f2f2';
    }
  }

  private pointerListener = async (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0) {
      return;
    }

    const button = this.mouseContainer.getChildByName('left-button-mouse') as Sprite;

    if (e.type === 'pointerdown') {
      button.tint = '0x0c58c6';
    } else {
      await delayFor(500);
      button.tint = '0xf2f2f2';
    }
  };

  private addMyPlayer(videoElement: HTMLVideoElement): void {
    const user = this.user();
    const social = this.social();
    if (user && social) {
      const { theme } = social;
      if (!theme) {
        this.error('Theme is undefined, cannot construct player');
        return;
      }

      this.mainPlayer = new OnboardingPlayer({
        store: this.store,
        renderer: this.app.renderer as Renderer,
        user: user,
        isPrimary: true,
        browserService: this.browserService,
        chatService: this.chatService,
        badgingService: this.badgingService,
        rtcStatsService: this.rtcStatsService,
        videoElement,
        emoteService: this.emoteService,
        y: this.windowHeight ? this.windowHeight / 2 : 500,
        x: this.windowWidth ? this.windowWidth / 2 - 150 : 500,
        sprites: this.sprites,
        showCursorIndicator: false,
        isOnboardingPlayer: true,
      });

      this.mainPlayer.name = formatUserName(user);
      // this.attachPlayerListeners(player);
      this.addPlayer(this.mainPlayer);
    }
  }

  private addMockPlayer(videoElement: HTMLVideoElement) {
    this.mockPlayer = new OnboardingPlayer({
      store: this.store,
      renderer: this.app.renderer as Renderer,
      user: { userId: '0', name: `Emily Sutherland`, firstName: 'Emily', lastName: 'Sutherland' },
      isPrimary: true,
      browserService: this.browserService,
      chatService: this.chatService,
      badgingService: this.badgingService,
      rtcStatsService: this.rtcStatsService,
      videoElement,
      emoteService: this.emoteService,
      y: this.windowHeight ? this.windowHeight / 2 : 500,
      x: this.windowWidth ? this.windowWidth / 2 + 150 : 500,
      sprites: this.sprites,
      ringColor: '#FFBD00',
      showCursorIndicator: false,
      isOnboardingPlayer: true,
    });

    this.mockPlayer.name = 'Onboarding Mock User';
    if (this.step()) {
      this.mockPlayer
        .setMockVideoElement(`./assets/video/onboarding-experience/${this.step()}.mp4`)
        .catch((e) => this.error('Failed to set mock video element.', e));
    }
    this.addPlayer(this.mockPlayer);
  }

  public addPlayer(p: OnboardingPlayer): void {
    p.zIndex = ZIndex.Player;
    p.visible = true;
    p.handleToggleGroup(true);
    this.roomContainer.addChild(p);
  }

  public updateMockPlayerVideo(step: string): void {
    this.mockPlayer?.updateVideoSrc(step);
  }

  private addPlayerMovement(): void {
    this.mainPlayer?.setLimitPosition(this.mockPlayer!.x + 100, this.mockPlayer!.y);
    this.app.stage.on('pointerdown', this.pointerDownListener as EventListener);
    this.app.stage.on('pointerup', this.pointerUpListener as EventListener);
    this.app.ticker.add(this.playerMovementTicker);
    this.mainPlayer?.addMovementControls();

    // Listener to tint component to navy
    this.app.stage.on('pointerup', this.pointerListener);
    this.app.stage.on('pointerdown', this.pointerListener);
    document.addEventListener('keydown', this.keydownListener);
    document.addEventListener('keyup', this.keydownListener);
  }

  private removePlayerMovement(): void {
    // Remove pointer event listeners
    this.app.stage.off('pointerdown', this.pointerDownListener as EventListener);
    this.app.stage.off('pointerup', this.pointerUpListener as EventListener);
    this.app.stage.off('pointerdown', this.pointerListener);
    this.app.stage.off('pointerup', this.pointerListener);

    // Remove keyboard event listeners
    document.removeEventListener('keydown', this.keydownListener);
    document.removeEventListener('keyup', this.keydownListener);

    this.app.ticker.remove(this.playerMovementTicker);
  }

  private playerMovementTicker = () => {
    if (this.mainPlayer && this.mockPlayer) {
      this.preventPlayerCollision(this.mainPlayer, this.mockPlayer);
    }
    this.mainPlayer?.animate();
    this.mockPlayer?.mockPlayerGroupRing(this.mainPlayer!.x + 100);
  };

  private mouseMoveTimeout: number | undefined;
  private pointerDownListener = (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0 || !this.mainPlayer || this.mouseMoveTimeout) {
      return;
    }
    this.mouseMoveTimeout = window.setInterval(() => {
      this.moveByMouse(e);
    }, 100);
  };

  private pointerUpListener = (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0) {
      return;
    }
    window.clearInterval(this.mouseMoveTimeout);
    this.mouseMoveTimeout = undefined;
  };

  private moveByMouse(e: FederatedPointerEvent): void {
    const speed = 50;
    const offset = 100;
    if (this.mainPlayer) {
      let positionChanged = false;

      if (this.mainPlayer.x < e.global.x - offset) {
        this.mainPlayer.targetX += speed;
        positionChanged = true;
      } else if (this.mainPlayer.x > e.global.x + offset) {
        this.mainPlayer.targetX -= speed;
        positionChanged = true;
      }

      if (this.mainPlayer.y < e.global.y - offset) {
        this.mainPlayer.targetY += speed;
        positionChanged = true;
      } else if (this.mainPlayer.y > e.global.y + offset) {
        this.mainPlayer.targetY -= speed;
        positionChanged = true;
      }

      // If position changed, check for collisions
      if (positionChanged && this.mockPlayer) {
        this.preventPlayerCollision(this.mainPlayer, this.mockPlayer);
      }

      // If auto-zoom is enabled and position changed, check if we need to adjust the zoom
      if (this.autoZoomEnabled && positionChanged && this.isAnyPlayerNearEdge()) {
        void this.zoomToFitAll();
      }
    }
  }

  private handleStep(currentStep: string): void {
    switch (currentStep) {
      case '1-3':
        this.orientationPositionPlayers();
        break;
      case '1-4':
        this.disableMovementPlayers();
        break;
      case '2-1':
        this.zoomControlsPosition();
        break;
      case '2-2':
        this.miniMapPosition();
        break;
      case '2-3':
        this.miniMapSelectorPosition();
        break;
      case '3-1':
        this.handleSmartBadgingInit();
        break;
      case '4-1':
        this.handleSmartBadgingPlayer();
        break;
      case '4-2':
        this.resetPosition();
        break;

      case '4-3':
        this.stageMagicPosition();
        break;
    }
  }

  private resetMainPlayerBadgePosition(): void {
    window.setTimeout(() => {
      const badgeElement = document.getElementById('badging-info');
      if (badgeElement && this.mainPlayer) {
        this.positionDOMElement(badgeElement, this.mainPlayer, -110, 180);
      }
    }, 0);
  }

  private resetMockPlayerBadgePosition(): void {
    window.setTimeout(() => {
      const badgeElement = document.getElementById('mock-badge');
      if (badgeElement && this.mockPlayer) {
        this.positionDOMElement(badgeElement, this.mockPlayer, 550, -100);
      }
    }, 0);
  }

  // endregion utilities

  // region zoom logic

  // methods for manual zoom
  public async zoomIn() {
    if (this.zoom < this.maxZoom) {
      await this.zoomTo(this.zoom + ZOOM_OFFSET, {
        ...this.snapZoomOptions,
        time: 500,
        removeOnInterrupt: true,
        ease: 'linear',
      });
    }
  }

  public async zoomOut(): Promise<void> {
    await this.zoomTo(this.zoom - ZOOM_OFFSET, {
      ...this.snapZoomOptions,
      time: 500,
      removeOnInterrupt: true,
      ease: 'linear',
    });
  }

  /**
   * Zoom to fit all visible elements in the viewport, including all visible children
   * This provides a comprehensive view of everything that should be visible to the user
   */
  public zoomToFitAll() {
    if (!this.viewport) {
      return;
    }

    // Get all visible children from the viewport and its children
    const allVisibleObjects = this.getAllVisibleObjects();

    // If we don't have any visible objects, fall back to default behavior
    if (!allVisibleObjects.length) {
      const defaultPos = this.getDefaultCenterPosition();
      const defaultZoom = this.minZoom + 0.2;

      this.viewport.moveCenter(defaultPos.x, defaultPos.y);
      this.viewport.animate({
        scale: defaultZoom,
        time: 800,
        ease: 'easeInOutSine',
      });
      return;
    }

    // Calculate the bounding box of all visible objects
    const bounds = this.calculateComprehensiveBoundingBox(allVisibleObjects);

    // TODO: fixed padding in pixels?
    // Add padding (20%)
    const padding = 0.2;
    bounds.x -= (bounds.width * padding) / 2;
    bounds.y -= (bounds.height * padding) / 2;
    bounds.width *= 1 + padding;
    bounds.height *= 1 + padding;

    // Calculate the center point of the bounding box
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    // Calculate scale needed to fit this content
    const scaleX = this.viewport.screenWidth / bounds.width;
    const scaleY = this.viewport.screenHeight / bounds.height;

    // Use the smaller scale to ensure everything fits, but respect min/max zoom
    const scale = Math.max(this.minZoom, Math.min(Math.min(scaleX, scaleY), this.maxZoom));

    // Set the calculated zoom level with animation
    this.viewport.animate({
      scale: scale,
      position: new Point(centerX, centerY),
      time: 800,
      ease: 'easeInOutSine',
    });
  }

  private positionDOMElement(domElement: HTMLElement, pixiElement: DisplayObject, offsetX = 0, offsetY = 0): void {
    if (!domElement || !pixiElement || !pixiElement.parent || !this.app?.view) {
      this.error('positionDOMElement called with invalid arguments or unmounted elements.');
      return;
    }

    try {
      const globalPosition = pixiElement.parent.toGlobal(pixiElement.position);
      // we would need to extract the canvas rect reference to reuse method
      const canvasRect = (this.app.view as HTMLCanvasElement).getBoundingClientRect();

      if (!canvasRect) {
        this.warn('Could not get canvas bounding client rect.');
        return;
      }

      // Map the global position to DOM coordinates relative to the viewport
      const domX = canvasRect.left + (globalPosition.x * canvasRect.width) / this.app.renderer.width;
      const domY = canvasRect.top + (globalPosition.y * canvasRect.height) / this.app.renderer.height;

      // Position the DOM element
      domElement.style.position = 'absolute';
      domElement.style.left = `${domX + offsetX}px`;
      domElement.style.top = `${domY + offsetY}px`;
    } catch (error) {
      this.error('Error positioning DOM element:', error);
    }
  }

  /**
   * Gets the set of key stable objects to use for bounding calculations
   * This ensures we don't get affected by video frames and animations
   */
  private getStableObjects(): DisplayObject[] {
    const stableObjects: DisplayObject[] = [];

    // Always include players if they exist
    if (this.mainPlayer && this.mainPlayer.visible) {
      stableObjects.push(this.mainPlayer);
    }

    if (this.mockPlayer && this.mockPlayer.visible) {
      stableObjects.push(this.mockPlayer);
    }

    // Add important containers, but only their container bounds, not their animated contents
    const importantContainers = [this.groupingContainer, this.stageMagicContainer];

    importantContainers.forEach((container) => {
      if (container && container.visible) {
        stableObjects.push(container);
      }
    });

    return stableObjects;
  }
  // Property to hold the last valid bounding box for smoothing
  private lastValidBoundingBox: Rectangle | null = null;

  public async zoomTo(zoomIn: number, options?: ISnapZoomOptions): Promise<void> {
    // round to 2 decimal places for better stability
    const zoom = Math.round(zoomIn * 100) / 100;

    // Check if we're already at/very close to the target zoom level
    // This can prevent tiny oscillations in zoom level
    if (Math.abs(this.zoom - zoom) < 0.01 && !options?.center) {
      return; // Skip if the zoom change is negligible and no center change
    }

    if (options) {
      // Create a reliable, stable snapZoom options object
      // Clone to avoid modifying the original options
      const snapZoomOptions = {
        ...options,
        // Convert from zoom scale to width (required by snapZoom)
        width: this.viewport.screenWidth / zoom,
        // Enforce strict completion to prevent interference
        removeOnComplete: true,
        removeOnInterrupt: true,
      };

      // Execute the combined zoom/pan operation with a try-catch for additional safety
      try {
        this.viewport.snapZoom(snapZoomOptions);

        // update state in ZoomPixiWorldControlsComponent
        this.zoomDirection$.next({ value: zoom > this.zoom ? 'in' : 'out' });

        window.clearTimeout(this.zoomTimeout);
        this.zoomTimeout = window.setTimeout(async () => {
          this.zoomDirection$.next({ value: 'none' });
          await this.zoomCleanup();
        }, snapZoomOptions.time || this.snapZoomOptions.time);
      } catch {
        // Fallback in case of an error with the animation
        this.viewport.setZoom(zoom, false);
        if (options.center) {
          this.viewport.moveCenter(options.center.x, options.center.y);
        }
      }
    } else {
      this.viewport.setZoom(zoom, true);
      await this.zoomCleanup();
    }
  }

  private async zoomCleanup(): Promise<void> {
    await delayFor(1000);
  }

  public resetZoom(): void {
    // Use zoomToFitAll instead of fixed zoom and position
    void this.zoomToFitAll();
  }

  public stopZoom(): void {
    if (this.zoomIntervalId) {
      clearInterval(this.zoomIntervalId);
      this.zoomIntervalId = null;
    }
  }

  public startZoom(zoomIn: boolean): void {
    // Immediately trigger the zoom action
    void this.handleZoom(zoomIn);

    // Set up an interval for continuous zooming
    this.zoomIntervalId = setInterval(() => {
      void this.handleZoom(zoomIn);
    }, 500);
  }

  public async handleZoom(zoomIn: boolean): Promise<void> {
    // Prevent overlapping zoom actions
    if (this.isZooming) {
      return;
    }
    try {
      this.isZooming = true;

      if (zoomIn) {
        await this.zoomIn();
      } else {
        await this.zoomOut();
      }
    } finally {
      // Allow the next zoom action
      this.isZooming = false;
    }
  }

  public autoZoomTooltip(): string {
    return `Auto Zoom is ${this.autoZoomEnabled ? 'on' : 'off'}`;
  }

  public toggleAutoZoom(forceValue?: boolean): void {
    this.autoZoomEnabled = forceValue !== undefined ? forceValue : !this.autoZoomEnabled;
    if (this.autoZoomEnabled) {
      // Initial fit when auto-zoom is enabled
      void this.zoomToFitAll();

      // Set up a ticker to periodically check if we need to adjust zoom
      this.app.ticker.add(this.autoFitTicker);
    } else {
      // Remove the ticker when auto-zoom is disabled
      this.app.ticker.remove(this.autoFitTicker);
    }
  }

  // Throttle the auto-fit checks to avoid performance issues
  private autoFitThrottleTime = 1000; // ms
  private lastAutoFitTime = 0;

  /**
   * Ticker function for auto-fitting content
   * Uses throttling to avoid excessive zoom adjustments
   */
  private autoFitTicker = () => {
    if (!this.autoZoomEnabled) {
      return;
    }

    const currentTime = Date.now();
    if (currentTime - this.lastAutoFitTime > this.autoFitThrottleTime) {
      this.lastAutoFitTime = currentTime;

      // Get stable objects for bounds calculation
      const visibleSprites = this.getStableObjects();

      // Check if any player is near the edge of the viewport
      // OR if the distance between player and other objects has changed significantly
      if (this.isAnyPlayerNearEdge() || this.hasDistanceChangedSignificantly(visibleSprites)) {
        void this.zoomToFitAll();
      }
    }
  };

  /**
   * Check if any important object is near the viewport edge
   */
  private isAnyPlayerNearEdge(): boolean {
    if (!this.viewport) {
      return false;
    }

    const bounds = this.viewport.getVisibleBounds();
    const padding = 100; // pixels from edge to trigger resize

    // Check main player
    if (this.mainPlayer) {
      if (this.isObjectNearEdge(this.mainPlayer, bounds, padding)) {
        return true;
      }
    }

    // Check mock player
    if (this.mockPlayer) {
      if (this.isObjectNearEdge(this.mockPlayer, bounds, padding)) {
        return true;
      }
    }

    // Check for important containers that might be near the edge
    const importantContainers = [
      this.groupingContainer,
      this.stageMagicContainer,
      this.keyboardContainer,
      this.mouseContainer,
    ];

    for (const container of importantContainers) {
      if (container && container.visible && this.isObjectNearEdge(container, bounds, padding)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a display object is near the edge of the provided bounds
   */
  private isObjectNearEdge(object: DisplayObject, bounds: Rectangle, padding: number): boolean {
    const objectBounds = object.getBounds();

    // Check if the object is near any edge
    return (
      objectBounds.x < bounds.x + padding ||
      objectBounds.y < bounds.y + padding ||
      objectBounds.x + objectBounds.width > bounds.x + bounds.width - padding ||
      objectBounds.y + bounds.height > bounds.y + bounds.height - padding
    );
  }

  /**
   * Gets default position for centering if no sprites are found
   */
  private getDefaultCenterPosition(): { x: number; y: number } {
    if (this.mainPlayer) {
      return { x: this.mainPlayer.x, y: this.mainPlayer.y };
    } else if (this.mockPlayer) {
      return { x: this.mockPlayer.x, y: this.mockPlayer.y };
    } else {
      return { x: this.w / 2, y: this.h / 2 };
    }
  }

  // endregion zoom logic

  // region grouping
  private createGroupingVideoContainer(): void {
    this.groupingContainer = new Container();
    this.groupingContainer.name = 'grouping-container';
    this.groupingContainer.sortableChildren = true;
    const groupingBorders = new Graphics()
      .lineStyle(MAP_BORDER, BORDER_COLOR)
      .drawRoundedRect(
        0,
        0,
        this.windowHeight ? this.windowHeight * 0.65 : 650,
        this.windowHeight ? this.windowHeight * 0.65 : 650,
        15,
      );

    groupingBorders.name = 'grouping-border';
    groupingBorders.sortableChildren = true;
    groupingBorders.zIndex = 1;
    this.hideGroupingRegion();

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/group_sizing.mp4';

    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();

      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);

      videoSprite.name = 'video-sprite';
      videoSprite.width = this.windowHeight ? this.windowHeight * 0.65 : 650;
      videoSprite.height = this.windowHeight ? this.windowHeight * 0.65 : 650;

      // Create a mask for the video sprite (rounded rectangle)
      const videoMask = new Graphics()
        .beginFill(0xffffff) // Mask fill color (not visible)
        .drawRoundedRect(
          0,
          0,
          this.windowHeight ? this.windowHeight * 0.65 : 650,
          this.windowHeight ? this.windowHeight * 0.65 : 650,
          15, // Rounded corner radius
        )
        .endFill();

      // Apply the mask to the video sprite
      videoSprite.mask = videoMask;

      this.groupingContainer.addChild(videoSprite, videoMask, groupingBorders);
      this.groupingContainer.x = this.windowWidth ? this.windowWidth * 0.1 : 250;
      this.groupingContainer.y = this.windowHeight ? this.windowHeight * 0.1 : 180;
      this.roomContainer.addChild(this.groupingContainer);
    });
  }

  private showGroupingRegion(): void {
    if (this.groupingContainer) {
      this.groupingContainer.visible = true;
    }
  }

  private hideGroupingRegion(): void {
    if (this.groupingContainer) {
      this.groupingContainer.visible = false;
    }
  }

  // endregion grouping logic

  // region stage magic

  private checkIfStageMagicExist(): void {
    const stageMagic = this.app.stage.getChildByName('stage-magic-container');

    if (!stageMagic) {
      this.createStateMagicVideoContainer(true);
    } else {
      this.showStageMagicRegion();
    }
  }

  private createStateMagicVideoContainer(startVisible: boolean): void {
    this.stageMagicContainer = new Container();
    this.stageMagicContainer.name = 'stage-magic-container';
    this.stageMagicContainer.sortableChildren = true;
    const stageMagicBorders = new Graphics()
      .lineStyle(MAP_BORDER, BORDER_COLOR)
      .drawRoundedRect(
        0,
        0,
        this.windowWidth ? this.windowWidth * 0.55 : 650,
        this.windowHeight ? this.windowHeight * 0.75 : 650,
        15,
      );
    stageMagicBorders.name = 'stage-magic-border';
    stageMagicBorders.sortableChildren = true;
    stageMagicBorders.zIndex = 1;
    this.stageMagicContainer.visible = startVisible;

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/stage_magic.mp4';

    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();

      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);

      videoSprite.name = 'video-sprite';
      videoSprite.width = this.windowWidth ? this.windowWidth * 0.55 : 1150;
      videoSprite.height = this.windowHeight ? this.windowHeight * 0.75 : 770;

      // Create a mask for the video sprite (rounded rectangle)
      const videoMask = new Graphics()
        .beginFill(0xffffff) // Mask fill color (not visible)
        .drawRoundedRect(
          0,
          0,
          this.windowWidth ? this.windowWidth * 0.55 : 650,
          this.windowHeight ? this.windowHeight * 0.75 : 650,
          15, // Rounded corner radius
        )
        .endFill();

      // Apply the mask to the video sprite
      videoSprite.mask = videoMask;

      this.stageMagicContainer.addChild(videoSprite, videoMask, stageMagicBorders);
      this.stageMagicContainer.x = this.windowWidth ? this.windowWidth * 0.55 : 1400;
      this.stageMagicContainer.y = this.windowHeight ? this.windowHeight * 0.05 : 30;
      this.roomContainer.addChild(this.stageMagicContainer);
    });
  }

  private showStageMagicRegion(): void {
    if (this.stageMagicContainer) {
      this.stageMagicContainer.visible = true;
    }
  }

  private hideStageMagicRegion(): void {
    if (this.stageMagicContainer) {
      this.stageMagicContainer.visible = false;
    }
  }

  // endregion stage magic

  // region keyboard component

  private createKeyboardComponent(): void {
    const keyWidth = 31;
    const keyHeight = 30;

    this.keyboardContainer = new Container();
    this.keyboardContainer.sortableChildren = true;
    this.keyboardContainer.name = 'keyboard-container';
    this.keyboardContainer.x = this.windowWidth ? this.windowWidth * 0.45 - 200 : 500;
    this.keyboardContainer.y = this.windowHeight ? this.windowHeight - 320 : 500;

    const upKeySprite = this.createKeySprite('keyboard_arrow_up', 220, 55, keyWidth, keyHeight);
    const downKeySprite = this.createKeySprite('keyboard_arrow_down', 220, 90, keyWidth, keyHeight);
    const leftKeySprite = this.createKeySprite('keyboard_arrow_left', 185, 90, keyWidth, keyHeight);
    const rightKeySprite = this.createKeySprite('keyboard_arrow_right', 255, 90, keyWidth, keyHeight);

    this.keyboardContainer.addChild(upKeySprite, downKeySprite, leftKeySprite, rightKeySprite);
    this.keyboardContainer.visible = false;
    this.app.stage.addChild(this.keyboardContainer);
  }

  private createKeySprite(name: string, x: number, y: number, width: number, height: number): Sprite {
    const keyboardImageSprite = Sprite.from(`../../../../assets/images/onboarding-experience/${name}.svg`);
    keyboardImageSprite.name = name;
    keyboardImageSprite.x = x;
    keyboardImageSprite.y = y;
    keyboardImageSprite.width = width;
    keyboardImageSprite.height = height;
    keyboardImageSprite.zIndex = 0;
    return keyboardImageSprite;
  }

  // endregion keyboard component

  // region mouse container

  private createMouseComponent(): void {
    const buttonWidth = 17.5;
    const buttonHeight = 20;
    const bottomWidth = 40;
    const bottomHeight = 32.5;

    this.mouseContainer = new Container();
    this.mouseContainer.sortableChildren = true;
    this.mouseContainer.name = 'mouse-container';
    this.mouseContainer.x = this.windowWidth ? this.windowWidth * 0.45 + 150 : 500;
    this.mouseContainer.y = this.windowHeight ? this.windowHeight - 290 : 500;

    const leftButtonSprite = Sprite.from('../../../../assets/images/onboarding-experience/left-mouse.svg');
    leftButtonSprite.name = 'left-button-mouse';
    leftButtonSprite.x = 0;
    leftButtonSprite.y = 25;
    leftButtonSprite.width = buttonWidth;
    leftButtonSprite.height = buttonHeight;
    leftButtonSprite.zIndex = 0;

    const rightButtonSprite = Sprite.from('../../../../assets/images/onboarding-experience/right-mouse.svg');
    rightButtonSprite.name = 'right-button-mouse';
    rightButtonSprite.x = 22;
    rightButtonSprite.y = 25;
    rightButtonSprite.width = buttonWidth;
    rightButtonSprite.height = buttonHeight;
    rightButtonSprite.zIndex = 0;

    const bottomSprite = Sprite.from('../../../../assets/images/onboarding-experience/bottom-mouse.svg');
    bottomSprite.name = 'bottom-mouse';
    bottomSprite.x = 0;
    bottomSprite.y = 50;
    bottomSprite.width = bottomWidth;
    bottomSprite.height = bottomHeight;
    bottomSprite.zIndex = 0;

    this.mouseContainer.addChild(leftButtonSprite, rightButtonSprite, bottomSprite);
    this.mouseContainer.visible = false;
    this.app.stage.addChild(this.mouseContainer);
  }

  private showMouseAndKeyboardContainer(): void {
    this.mouseContainer.visible = true;
    this.keyboardContainer.visible = true;
  }

  private hideMouseAndKeyboardContainer(): void {
    this.mouseContainer.visible = false;
    this.keyboardContainer.visible = false;
  }

  // endregion mouse container

  // region steps player position

  private orientationPositionPlayers(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.15, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.85, 0.5);

      this.mainPlayer.setPosition(mainPlayerPosition.x, this.mainPlayer!.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x, this.mockPlayer!.y);
      this.mockPlayer.handleToggleGroup(false);

      this.addPlayerMovement();
      this.showMouseAndKeyboardContainer();
    }
  }

  private resetPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);
    }
  }

  private disableMovementPlayers(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.75, 0);
      const mockPlayerPosition = this.calculatePosition(0.75, 0);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, this.mainPlayer!.y);
      this.mainPlayer.handleToggleGroup(true);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, this.mockPlayer!.y);
      this.mockPlayer.handleToggleGroup(true);

      this.removePlayerMovement();
      this.hideMouseAndKeyboardContainer();
    }
  }

  private zoomControlsPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.2, 0.75);
      const mockPlayerPosition = this.calculatePosition(0.2, 0.7);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);

      void delayFor(1000).then(() => {
        this.mainPlayer?.setLimitPosition(mockPlayerPosition.x - 200, mockPlayerPosition.y);
      });

      this.mockPlayer.handleToggleGroup(true);

      this.hideZoom.set(false);
      this.inDisabled.set(false);
      this.outDisabled.set(false);
    }
  }

  private miniMapPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.6, 0.65);
      const mockPlayerPosition = this.calculatePosition(0.6, 0.6);

      this.mainPlayer.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);
      this.mainPlayer.handleToggleGroup(true);

      this.mockPlayer.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
    }
  }

  private miniMapSelectorPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.7, 0.2);
      const mockPlayerPosition = this.calculatePosition(0.7, 0.15);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);
      this.mainPlayer.handleToggleGroup(true);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);
    }
  }

  private handleSmartBadgingInit(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);

      this.mockPlayer!.handlePointerOver(this.zoom);

      this.hideZoom.set(true);
      this.inDisabled.set(true);
      this.outDisabled.set(true);
    }
  }

  private handleSmartBadgingPlayer(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mainPlayerPosition.x + 150, mockPlayerPosition.y);

      this.resetMainPlayerBadgePosition();
    } else {
      window.setTimeout(() => {
        this.handleSmartBadgingPlayer();
      }, 100);
    }
  }

  private stageMagicPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.15, 0.3);
      const mockPlayerPosition = this.calculatePosition(0.15, 0.4);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
    }
  }

  private calculatePosition(percentageX: number, percentageY: number): { x: number; y: number } {
    const x = this.windowWidth ? this.windowWidth * percentageX : 0;
    const y = this.windowHeight ? this.windowHeight * percentageY : 0;
    return { x, y };
  }

  // endregion  steps player position

  private lastAutoFitDistance = 0; // Last distance to the furthest object
  /**
   * Checks if the distance to the furthest object has changed significantly
   * This helps stabilize auto-zoom by only triggering when meaningful changes occur
   */
  private hasDistanceChangedSignificantly(sprites: DisplayObject[]): boolean {
    if (!this.mainPlayer) {
      return false;
    }

    // Get the main player's position
    const playerPos = {
      x: this.mainPlayer.x,
      y: this.mainPlayer.y,
    };

    // Calculate distance to each object and find the furthest one
    let maxDistance = 0;

    sprites.forEach((sprite) => {
      // Skip the main player
      if (sprite === this.mainPlayer) {
        return;
      }

      // Get sprite's center point
      const bounds = sprite.getBounds();
      const spriteCenter = {
        x: bounds.x + bounds.width / 2,
        y: bounds.y + bounds.height / 2,
      };

      // Calculate distance from player to this sprite
      const dx = spriteCenter.x - playerPos.x;
      const dy = spriteCenter.y - playerPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Keep track of the furthest object
      if (distance > maxDistance) {
        maxDistance = distance;
      }
    });

    // Check if distance has changed significantly (>20%)
    const significantChange = Math.abs(maxDistance - this.lastAutoFitDistance) > this.lastAutoFitDistance * 0.2;

    // Update stored distance
    this.lastAutoFitDistance = maxDistance;

    return significantChange;
  }

  /**
   * Gets all visible display objects in the scene
   * This includes players, containers, and other UI elements
   */
  private getAllVisibleObjects(): DisplayObject[] {
    return [
      this.mainPlayer,
      this.mockPlayer,
      this.groupingContainer,
      this.stageMagicContainer,
      this.keyboardContainer,
      this.mouseContainer,
    ].filter(Boolean) as DisplayObject[];
  }

  /**
   * Calculates a comprehensive bounding box that includes all visible objects
   * This is used for the zoomToFitAll function to ensure everything is properly visible
   */
  private calculateComprehensiveBoundingBox(sprites: DisplayObject[]): Rectangle {
    if (!sprites.length) {
      return new Rectangle(0, 0, this.w, this.h);
    }

    // Start with the first sprite's bounds
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    // Calculate the union of all sprite bounds
    sprites.forEach((sprite) => {
      // Get the bounds in world coordinates
      const bounds = sprite.getBounds();

      // Skip sprites with extremely large or negative dimensions/positions
      if (
        bounds.width > 10000 ||
        bounds.height > 10000 ||
        bounds.x < -5000 ||
        bounds.y < -5000 ||
        bounds.x > 10000 ||
        bounds.y > 10000
      ) {
        return;
      }

      // Also skip sprites that are too small to be significant
      if (bounds.width < 5 && bounds.height < 5) {
        return;
      }

      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    });

    // If we somehow ended up with invalid bounds (all sprites were filtered out)
    if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
      // Use the last valid box if we have one, otherwise default world size
      if (this.lastValidBoundingBox) {
        return this.lastValidBoundingBox.clone();
      }
      return new Rectangle(0, 0, this.w, this.h);
    }

    // Apply smoothing if we have a previous bounding box
    if (this.lastValidBoundingBox) {
      // Apply smoothing with a balanced weight (70% previous, 30% new)
      // This creates smoother transitions between frames
      const result = new Rectangle(
        this.lastValidBoundingBox.x * 0.7 + minX * 0.3,
        this.lastValidBoundingBox.y * 0.7 + minY * 0.3,
        this.lastValidBoundingBox.width * 0.7 + (maxX - minX) * 0.3,
        this.lastValidBoundingBox.height * 0.7 + (maxY - minY) * 0.3,
      );

      // Store this as our new last valid bounding box
      this.lastValidBoundingBox = result.clone();

      return result;
    }

    // First time calculation - create a new rectangle and store it
    const result = new Rectangle(minX, minY, maxX - minX, maxY - minY);
    this.lastValidBoundingBox = result.clone();

    return result;
  }
}
